import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { IVector3 } from '@seddi/math-v2';

type LookAt = {
  origin: IVector3;
  target: IVector3;
};

/**
 * Garment target space coordinates
 */
type ControlledCamera = LookAt & {
  id: string;
};

export type SizeRangeState = {
  cameraLookAtDistance: Record<string, number>;
  cameraRotation: number;
  cameraZoom: number;
  controlledCamera: ControlledCamera | undefined;
  visibility: Record<string, boolean> | undefined;
};

const initialState: SizeRangeState = {
  cameraLookAtDistance: {},
  cameraRotation: 0,
  cameraZoom: 0,
  controlledCamera: undefined,
  visibility: undefined,
};

const sizeRangeSlice = createSlice({
  name: 'sizeRange',
  initialState,
  reducers: {
    clear: state => {
      state.cameraLookAtDistance = {};
      state.cameraRotation = 0;
      state.cameraZoom = 0;
      state.visibility = undefined;
    },
    setCameraRotation: (state, action: PayloadAction<number>) => {
      state.cameraRotation = action.payload;
      state.controlledCamera = undefined;
    },
    setCameraZoom: (state, action: PayloadAction<number>) => {
      state.cameraZoom = action.payload;
      state.controlledCamera = undefined;
    },
    setControlledCamera: (state, action: PayloadAction<ControlledCamera>) => {
      state.controlledCamera = action.payload;
    },
    setSizeCameraLookAtDistance: (
      state,
      action: PayloadAction<{ size: string; distance: number }>,
    ) => {
      state.cameraLookAtDistance[action.payload.size] = action.payload.distance;
    },
    setSizeVisibility: (state, action: PayloadAction<{ size: string; visibility: boolean }>) => {
      state.visibility ??= {};
      state.visibility[action.payload.size] = action.payload.visibility;
    },
    setVisibility: (state, action: PayloadAction<Record<string, boolean> | undefined>) => {
      state.visibility = action.payload;
    },
  },
});

export const {
  clear,
  setCameraRotation,
  setCameraZoom,
  setControlledCamera,
  setSizeCameraLookAtDistance,
  setSizeVisibility,
  setVisibility,
} = sizeRangeSlice.actions;

export const sizeRangeReducer = sizeRangeSlice.reducer;
